import { z } from 'zod';
import type { CanonicalModel } from './canonical';
import {
  ConfidenceSchema,
  type Confidence,
  type DataClassification,
  type ScoringMethod,
  type Severity,
} from './enums';
import type { CandidateFindingDraft } from './finding';
import type { ResolvedThreatPattern } from './pack';

/**
 * The pluggable scoring seam (DESIGN_SPEC §9.3) and the one method that ships.
 *
 * "STRIDE classifies; scoring scores." The pipeline, the review screen and every
 * report read `severity_normalised` and never care which method produced it, so
 * adding OWASP Risk Rating or a client's own impact × likelihood matrix later is
 * a new `Scorer`, not a schema change. A client can also re-score an assessment
 * with a different method without re-running identification.
 *
 * §9.3 says: build the abstraction in MVP, ship one method. sds-00 built the
 * abstraction; `sds-05g` ships QRS materiality (docs/DECISIONS.md ZD-058).
 *
 * ## The measurement that shaped the method, because it is not a caveat
 *
 * On the corpus the band is **constant within every firing pattern** unless
 * asset criticality reaches the finding. Of the nine dimensions ZD-011 names,
 * **two have a source that can vary by system** and only one of those is
 * populated today. Everything else is either authored on the pattern — which
 * makes it a property of the threat class rather than of the client — or has no
 * source at all. So this method is deliberately narrower than ZD-011
 * anticipated: it scores what can be established, and records the rest as
 * unestablished rather than inventing it.
 */

/**
 * A generic risk band, shared across every dimension so scoring methods read
 * one vocabulary rather than each inventing its own scale.
 */
export const RISK_BANDS = ['low', 'medium', 'high'] as const;
export const RiskBandSchema = z.enum(RISK_BANDS);
export type RiskBand = z.infer<typeof RiskBandSchema>;

/**
 * What a dimension says when it says no band — and there are **two** such
 * things, which is the whole reason this type exists (ZD-058 §2).
 *
 * The vocabulary is `SLOT_STATES`', deliberately: a statement slot and a risk
 * dimension are answering the same question about the same model, and two
 * vocabularies for one distinction is two rules.
 *
 *  - `unestablished` — nobody worked it out. Nothing in the pattern states it
 *    and nothing in the client's model implies it.
 *  - `not_applicable` — there is nothing here to state, because the lens does
 *    not assess this dimension at all.
 *
 * **Until `sds-05g` both were spelled `null` on three dimensions and `low` on
 * the other five**, and the second is worse than the first: `low` is a claim
 * ("this rarely happens", "the loss would be small") that no document made,
 * which is exactly ZD-019's manufactured assertion sitting in a required field.
 * `lowestRiskDimensions` wrote it on five dimensions while its own comment said
 * it was "never a claim about actual risk"; in the type it was one.
 *
 * **`not_applicable` has no producer yet, and that is stated rather than left
 * to be discovered.** Nothing declares which dimensions a given `analysis_lens`
 * assesses, so nothing can honestly write it. It ships anyway, because the
 * alternative is that the first lens to declare its dimensions writes
 * `unestablished` over a question that does not arise — and by then the stored
 * findings carrying that false claim are indistinguishable from the true ones,
 * which is the retrofit cost ZD-021 exists to avoid paying twice. **Reopen
 * when** a lens declares its assessed dimensions.
 */
export const DIMENSION_ABSENCES = ['unestablished', 'not_applicable'] as const;
export const DimensionAbsenceSchema = z.enum(DIMENSION_ABSENCES);
export type DimensionAbsence = z.infer<typeof DimensionAbsenceSchema>;

/** A band, or the honest statement that there is none. */
export const RiskDimensionSchema = z.union([RiskBandSchema, DimensionAbsenceSchema]);
export type RiskDimension = z.infer<typeof RiskDimensionSchema>;

/** Whether a dimension carries a band at all. */
export function isBand(value: RiskDimension): value is RiskBand {
  return value !== 'unestablished' && value !== 'not_applicable';
}

/**
 * The stronger of two accounts of one dimension.
 *
 * Used where a pattern states a band and the client's model implies another —
 * `asset_criticality` is the only one today. It takes the stronger rather than
 * the more specific, because the alternative is a pattern's authored floor
 * being *lowered* by a client's model, and a threat class does not become less
 * damaging because one system's documents are thin.
 *
 * An absence never wins: `unestablished` beside a band is the band, which is
 * how a dimension only one source can establish still reaches the score.
 */
export function strongerDimension(a: RiskDimension, b: RiskDimension): RiskDimension {
  if (!isBand(a)) return b;
  if (!isBand(b)) return a;
  return ORDER[a] >= ORDER[b] ? a : b;
}

/**
 * The typed inputs every scoring method is a *view* over (docs/DECISIONS.md
 * ZD-011). Persisting these on the finding rather than a method's derived
 * `scoring_detail` bag means DREAD, OWASP RR or a FAIR-aligned range can be
 * added later with no schema change and no re-running of identification.
 *
 * **Every dimension is three-valued** (ZD-058 §2). Five of them were required
 * `low | medium | high` with no honest value for *nobody established this*, and
 * measured against the packs that is not an edge case: **0 of 65 patterns state
 * `control_strength` or `secondary_loss_magnitude`**, and 15 state no
 * `threat_event_frequency`. A required band there manufactures an assertion on
 * every finding in the system.
 *
 * `confidence` stays a plain `Confidence` because it is **derived** rather than
 * established (§8.4, CLAUDE.md principle 5) — `deriveConfidence` always returns
 * one, so it has no absent state, and giving it one would invite a writer.
 */
export const RiskDimensionsSchema = z.object({
  threat_event_frequency: RiskDimensionSchema,
  control_strength: RiskDimensionSchema,
  primary_loss_magnitude: RiskDimensionSchema,
  secondary_loss_magnitude: RiskDimensionSchema,
  asset_criticality: RiskDimensionSchema,
  discoverability: RiskDimensionSchema,
  reproducibility: RiskDimensionSchema,
  affected_scope: RiskDimensionSchema,
  confidence: ConfidenceSchema,
});
export type RiskDimensions = z.infer<typeof RiskDimensionsSchema>;

/** The dimensions, by name, so a detail bag can list them without a literal. */
export const RISK_DIMENSION_NAMES = [
  'threat_event_frequency',
  'control_strength',
  'primary_loss_magnitude',
  'secondary_loss_magnitude',
  'asset_criticality',
  'discoverability',
  'reproducibility',
  'affected_scope',
] as const satisfies readonly Exclude<keyof RiskDimensions, 'confidence'>[];
export type RiskDimensionName = (typeof RISK_DIMENSION_NAMES)[number];

/**
 * What a **pack author** may state about a pattern's inherent risk.
 *
 * Not `RiskDimensions.partial()`, and the three differences are each a rule
 * that was previously enforced by nobody (ZD-058 §5).
 *
 * **No `confidence`.** `RiskDimensions.partial()` admitted it, so a pattern
 * could have written `confidence: 'high'` and parsed — a self-reported
 * confidence in the one place CLAUDE.md principle 5 says none may exist. **0 of
 * 65 patterns do it**, so this is a hole found before anything crossed it; the
 * strict object closes it *by construction* rather than by a refinement, which
 * is the difference between a rule and a check.
 *
 * **No `control_strength`.** It is a property of the client's system, not of
 * the threat class, so a pattern stating it would be asserting something about
 * every system it ever fires on. **0 of 65 state it**, and the field going
 * means none can.
 *
 * **`primary_loss_magnitude` is required.** It is the only impact input QRS
 * materiality has, so a pattern omitting it produces a finding nothing can
 * band. All 65 state it today, which makes this the cheap moment to make it a
 * schema property rather than a corpus accident — ZD-055's rule that what a
 * check has caught is a fact about its inputs, and what it *can* catch is a
 * property of what the code admits.
 *
 * **No absence members.** A pack author omits a key; spelling `unestablished`
 * would be a second way to say the same thing, which is the trapdoor ZD-036 §2
 * closes one level down.
 */
export const PatternScoringInputsSchema = z.strictObject({
  primary_loss_magnitude: RiskBandSchema,
  threat_event_frequency: RiskBandSchema.optional(),
  secondary_loss_magnitude: RiskBandSchema.optional(),
  asset_criticality: RiskBandSchema.optional(),
  discoverability: RiskBandSchema.optional(),
  reproducibility: RiskBandSchema.optional(),
  affected_scope: RiskBandSchema.optional(),
});
export type PatternScoringInputs = z.infer<typeof PatternScoringInputsSchema>;

export interface ScoringInput {
  draft: CandidateFindingDraft;
  pattern: ResolvedThreatPattern;
  model: CanonicalModel;
  /** The derived confidence — carried for provenance, never a scoring input. */
  confidence: Confidence;
  dimensions: RiskDimensions;
}

export interface ScoringResult {
  severity_normalised: Severity;
  scoring_method: ScoringMethod;
  scoring_detail: Record<string, unknown>;
  dimensions: RiskDimensions;
}

export type Scorer = (input: ScoringInput) => ScoringResult;

/**
 * Every dimension unestablished — the honest state for a finding nothing has
 * scored, and for one whose documents said nothing.
 *
 * Replaces `lowestRiskDimensions`, which wrote `low` on five bands. The rename
 * is the correction: *lowest* describes a value, and there was no value to
 * describe.
 */
export function unestablishedRiskDimensions(confidence: Confidence): RiskDimensions {
  return {
    threat_event_frequency: 'unestablished',
    control_strength: 'unestablished',
    primary_loss_magnitude: 'unestablished',
    secondary_loss_magnitude: 'unestablished',
    asset_criticality: 'unestablished',
    discoverability: 'unestablished',
    reproducibility: 'unestablished',
    affected_scope: 'unestablished',
    confidence,
  };
}

/**
 * The scorer for a caller with nothing to score with.
 *
 * **ZD-005 justified this by a window in the pipeline — *between
 * `domain_analysis_complete` and `scoring_complete`, where a finding exists and
 * genuinely has no score* — and `sds-05y` abolished that window** by deleting
 * the second of those states (docs/DECISIONS.md ZD-093). `runDomainAnalysis`
 * passes `qrsMaterialityScorer` into `validateFinding`, so a persisted finding
 * is banded at the moment it is created and the gap ZD-005 described is not one
 * the pipeline has.
 *
 * **The scorer survives the window, on ZD-005's other half**, which is the
 * durable one: rather than defaulting a finding to `informational` under a
 * method that never ran, it carries the explicit method `unscored`. That is the
 * honest answer for a caller with no evaluation behind it — a hand-created
 * finding, an approver edit — and it is why `score` stays optional on
 * `FindingValidationContext` rather than becoming required. Making it required
 * would force such a caller to name a scorer it has no inputs for, which is the
 * fabricated band ZD-005 refused, arriving by ceremony.
 *
 * **`unscored` is a statement about the producer, not about the data**, and
 * ZD-058 §6 turns on keeping it that way: a finding whose documents were thin
 * is *scored*, with its unestablished dimensions named. It is `unscored` only
 * while the stage has not run on it. That is `ReportSectionState`'s
 * `unavailable` versus `empty`, one object along.
 */
export const unscoredScorer: Scorer = (input) => ({
  severity_normalised: 'informational',
  scoring_method: 'unscored',
  scoring_detail: { status: 'not_scored' },
  dimensions: unestablishedRiskDimensions(input.confidence),
});

export function isUnscored(finding: { scoring_method: ScoringMethod }): boolean {
  return finding.scoring_method === 'unscored';
}

// ---------------------------------------------------------------------------
// QRS materiality
// ---------------------------------------------------------------------------

const ORDER: Record<RiskBand, number> = { low: 1, medium: 2, high: 3 };

/** The stronger of two bands; the conservative direction (CLAUDE.md principle 9). */
function stronger(a: RiskBand | null, b: RiskBand | null): RiskBand | null {
  if (a === null) return b;
  if (b === null) return a;
  return ORDER[a] >= ORDER[b] ? a : b;
}

/**
 * The rubric. Rows are impact, columns likelihood.
 *
 * Written as a table rather than as arithmetic on purpose: DESIGN_SPEC §9.2's
 * whole complaint about unguided DREAD is that a score nobody can point at
 * wanders between assessors, and a table is a thing a reviewer can disagree
 * with one cell at a time.
 */
const MATERIALITY: Record<RiskBand, Record<RiskBand, Severity>> = {
  high: { low: 'medium', medium: 'high', high: 'critical' },
  medium: { low: 'low', medium: 'medium', high: 'high' },
  low: { low: 'informational', medium: 'low', high: 'medium' },
};

/**
 * What the band rests on when likelihood is unestablished.
 *
 * **Not the `medium` column, even though it agrees with it numerically.** The
 * claim is different — *we ranked this on impact alone* rather than *we judged
 * it moderately likely* — and `ranked_on` is what carries the difference into
 * the detail bag and the report. Written as its own map so that a later change
 * to the `medium` column cannot silently redefine what an unestablished
 * likelihood means.
 */
const IMPACT_ALONE: Record<RiskBand, Severity> = {
  high: 'high',
  medium: 'medium',
  low: 'low',
};

/** A `default_scoring_inputs` that states no impact at all. */
export class UnscoreableFindingError extends Error {
  constructor(reason: string) {
    super(
      `QRS materiality cannot band this finding: ${reason}. A band computed without an impact ` +
        `input would be a number about nothing, and a client reads it as a number about their ` +
        `system`,
    );
    this.name = 'UnscoreableFindingError';
  }
}

export interface MaterialityView {
  severity_normalised: Severity;
  /** The composed impact band, after asset criticality. */
  impact: RiskBand;
  likelihood: RiskDimension;
  /** Which halves of the rubric actually ran. */
  ranked_on: RiskDimensionName[];
  /** Every dimension carrying no band — the finding's own account of its gaps. */
  unestablished: RiskDimensionName[];
}

/**
 * QRS materiality: impact × likelihood, and **not** × confidence.
 *
 * ## Confidence is reported beside the band and never inside it
 *
 * CLAUDE.md said `impact × likelihood × confidence` and ZD-058 §1 amends it, on
 * a measurement rather than a preference. Derived confidence is
 * **anti-correlated with severity** on the corpus: of the 42 findings this
 * method bands `critical` or `high`, **39 carry low confidence**, while 9 of
 * the 15 high-confidence findings sit in the bottom two bands. Multiplying
 * would empty the top of the register and promote the trivia — and the thing
 * driving it would be whether the client's documents happened to be explicit,
 * not whether the threat matters. That is CLAUDE.md principle 9's stated
 * primary failure mode arriving as arithmetic.
 *
 * It would also price the same uncertainty twice: **50 of 102** findings are
 * already `conditional` and already say what they are waiting on.
 *
 * And it discriminates nothing while looking as though it does —
 * `deriveConfidence` returns `medium` on **0 of 102**, because
 * `predicate_basis: 'boost'` never occurs.
 *
 * Severity answers *how much would this matter*; confidence answers *how sure
 * are we*. A client needs both, and a product is a multiplication of neither.
 *
 * ## It reads the dimensions and nothing else
 *
 * The signature is the guarantee. A method that reached for the model or the
 * pattern would not be a *view* over persisted dimensions, and re-scoring an
 * assessment under a second method — ZD-011's whole point — would stop being
 * possible without re-running identification.
 */
export function qrsMateriality(dimensions: RiskDimensions): MaterialityView {
  const unestablished = RISK_DIMENSION_NAMES.filter((name) => !isBand(dimensions[name]));

  const primary = dimensions.primary_loss_magnitude;
  if (!isBand(primary)) {
    throw new UnscoreableFindingError(
      `primary_loss_magnitude is ${primary}, and no other dimension can stand in for it`,
    );
  }

  const criticality = dimensions.asset_criticality;
  /*
   * `stronger`, not an average: an established sensitive asset may raise the
   * impact and may never lower it, so an *unestablished* criticality cannot
   * quietly reduce a band either. Both halves of that are the conservative
   * direction, which is the one CLAUDE.md principle 9 names.
   */
  const impact = stronger(primary, isBand(criticality) ? criticality : null)!;

  const likelihood = dimensions.threat_event_frequency;
  return isBand(likelihood)
    ? {
        severity_normalised: MATERIALITY[impact][likelihood],
        impact,
        likelihood,
        ranked_on: ['primary_loss_magnitude', 'threat_event_frequency'],
        unestablished,
      }
    : {
        severity_normalised: IMPACT_ALONE[impact],
        impact,
        likelihood,
        ranked_on: ['primary_loss_magnitude'],
        unestablished,
      };
}

/**
 * QRS materiality behind the `Scorer` seam.
 *
 * The detail bag carries `unestablished` because a finding banded from one
 * input and one banded from four are otherwise indistinguishable downstream,
 * which is how a thin score becomes a confident one (ZD-058 §6). The rule is
 * `PatternMatchSchema`'s, one stage along — an undecided verdict must name what
 * it could not decide.
 *
 * **The `scoring_complete` guard read it until ZD-093 deleted that stage**, and
 * the rule outlived the reader for a reason worth stating where it can be
 * acted on: the property is a *scorer's*, not a *finding's*, so the population
 * it is asserted over is this module's exported scorers — which grow — and not
 * the findings of one assessment. `scoring.test.ts` holds that conformance
 * test, and it discovers its population rather than being handed one.
 */
export const qrsMaterialityScorer: Scorer = (input) => {
  const view = qrsMateriality(input.dimensions);
  return {
    severity_normalised: view.severity_normalised,
    scoring_method: 'qrs_materiality',
    scoring_detail: {
      impact: view.impact,
      likelihood: view.likelihood,
      ranked_on: view.ranked_on,
      unestablished: view.unestablished,
    },
    dimensions: input.dimensions,
  };
};

/**
 * What a scored finding must be able to say about its own gaps.
 *
 * **This was read by the `scoring_complete` guard, on the reasoning that it is
 * a property of the *stage* having run honestly rather than of one method.**
 * ZD-093 deleted that stage and the reasoning was the thing wrong with it: the
 * guard's clause was empty **by construction**, because every scorer that bands
 * anything satisfies this, so it could never refuse a stage that had run. It is
 * a property of a *scorer*, and the conformance test in `scoring.test.ts`
 * asserts it over the scorers this module exports.
 *
 * **Returns `false` for an unscored finding, deliberately.** That is not an
 * exception to the rule but the rule stated totally: a scorer names its gaps
 * exactly when it produced a band. The conformance test asserts the
 * biconditional, so `unscoredScorer` is a positive case rather than a
 * name-based exclusion — and a scorer added later cannot escape by being
 * neither.
 */
export function scoringDetailNamesItsGaps(finding: {
  scoring_method: ScoringMethod;
  scoring_detail: Record<string, unknown>;
  risk_dimensions: RiskDimensions;
}): boolean {
  if (isUnscored(finding)) return false;
  const named = finding.scoring_detail.unestablished;
  if (!Array.isArray(named)) return false;
  const expected = RISK_DIMENSION_NAMES.filter((name) => !isBand(finding.risk_dimensions[name]));
  return (
    named.length === expected.length && expected.every((name) => named.includes(name as unknown))
  );
}

/**
 * How sensitive the data a finding touches is (ZD-058 §3).
 *
 * The only dimension with a source in the **client's** model rather than in the
 * pack, which is why the band varies by system at all — 3 of the 24 firing
 * patterns, and 0 without it.
 *
 * **The map is total over `DataClassification`**, so adding a classification is
 * a compile error here rather than a silent `unestablished` on every asset that
 * carries it. `scoring.test.ts` asserts that the cells banded `high` are
 * exactly `SENSITIVE_DATA_CLASSIFICATIONS` — the packs' own list of what
 * matters — as a check rather than by construction, because building the map
 * from that list would make the correspondence unfalsifiable.
 *
 * `unknown` contributes nothing: it is ZD-019's *nobody said* member, and
 * reading it as `low` would turn an unclassified asset into a statement that
 * its loss would be small.
 */
const CLASSIFICATION_BAND: Record<DataClassification, RiskBand | null> = {
  public: 'low',
  internal: 'medium',
  pii: 'high',
  financial: 'high',
  health: 'high',
  secret: 'high',
  /* ZD-019's *nobody said* member. `null` is the only honest cell here. */
  unknown: null,
};

export function assetCriticality(
  model: CanonicalModel,
  assetIds: readonly string[],
): RiskDimension {
  let band: RiskBand | null = null;
  let sawAsset = false;
  for (const asset of model.assets) {
    if (!assetIds.includes(asset.asset_id)) continue;
    sawAsset = true;
    band = stronger(band, CLASSIFICATION_BAND[asset.data_classification]);
  }
  /*
   * A finding that touches an asset whose classification nobody stated is not a
   * finding about nothing — but it is not one this dimension can band either,
   * so both cases are the same absence and neither invents a value.
   */
  return sawAsset && band !== null ? band : 'unestablished';
}
